<template>
  <VFooter
    id="footer"
    :fixed="false"
    color="white"
    height="unset"
    class="pa-0 footer-flex"
  >
    <!-- Tagline section. Recreates V2's default-gutter row/col layout explicitly:
         container padding 12px (V4's .v-container default is 16px → pa-3),
         row margin -12px (V2 default-gutter row → ma-n3), col padding 12px
         (V2 default-gutter col → pa-3). Keep this explicit instead of using
         `v2-gutters`: the compat helper restores the shared V2 row primitive,
         while this footer strip needs a narrower one-off composition.
         hideTagline removes the whole container, not just the image: the band carries its
         own borders and padding, and the SparkIt landing page needs its content to meet the
         grey footer flush. AB#62168 -->
    <VContainer v-if="!hideTagline" fluid class="x-borders pa-3" data-testid="footer-tagline">
      <VRow align="center" justify="center" class="py-3 ma-n3">
        <VCol cols="12" sm="auto" order="2" order-sm="1" class="pa-3 text-center text-sm-right">
          <!-- One image, sized per breakpoint, from a cacheable file: below Vite's inline limit
               it was a ~5 KB data: URI, twice, in every page's HTML. -->
          <img
            alt="Tagline"
            src="~/assets/images/SU_TAGLINE.svg?no-inline"
            title="Tagline"
            loading="lazy"
            class="tagline mb-n1"
          >
        </VCol>
      </VRow>
    </VContainer>

    <VContainer fluid class="pa-0 pt-12 pb-6 background-grey">
      <VContainer class="pa-0">
        <VRow no-gutters justify="center">
          <VCol cols="12" lg="8" style="position: relative;">
            <!-- need ref for brekpoint because of vue bug https://github.com/vuejs/vue/issues/10260 -->
            <VRow ref="footer" no-gutters>
              <VCol cols="6" :sm="isDemonstrator ? 4 : 3" lg="3" class="mb-12 mb-sm-0">
                <FooterSection :header="$t('footer.label.help')" :index="1">
                  <FooterButton to="/help/contact-us">
                    {{ $t('footer.link.contact') }}
                  </FooterButton>
                  <FooterButton to="/help/orders-and-exchanges">
                    {{ $t('footer.link.orders') }}
                  </FooterButton>
                  <FooterButton to="/help/shipping">
                    {{ $t('footer.link.shipping') }}
                  </FooterButton>
                  <FooterButton to="/help/faqs">
                    {{ $t('footer.link.faqs') }}
                  </FooterButton>
                  <FooterButton data-acsb-custom-trigger="true">
                    {{ $t('footer.link.accessibility') }}
                  </FooterButton>
                  <FooterButton v-if="isDemonstrator" :href="$t('footer.link.userpilot')" rel="noreferrer">
                    {{ $t('footer.link.resourceHub') }}
                  </FooterButton>
                </FooterSection>
              </VCol>

              <VCol cols="6" :sm="isDemonstrator ? 4 : 3" lg="3" class="mb-12 mb-sm-0">
                <FooterSection :header="$t('footer.label.company')" :index="2">
                  <FooterButton to="/company/about-us">
                    {{ $t('footer.link.aboutUs') }}
                  </FooterButton>
                  <FooterButton to="/company/making-a-difference">
                    {{ $t('footer.link.difference') }}
                  </FooterButton>
                  <FooterButton to="/company/angel-policy">
                    {{ $t('footer.link.angel') }}
                  </FooterButton>
                  <!-- The translation carries its own <a> (a per-market survey URL), so it is not
                       wrapped in FooterButton: that renders a <button> here, and an <a> nested in a
                       <button> is invalid interactive content (WCAG 4.1.2). The anchor is styled
                       to match its FooterButton siblings instead. -->
                  <div v-if="isDemonstrator" class="footer-link-suggestion" data-testid="footer-link-suggestion">
                    <VHtml :html="$t('footer.link.suggestion')" />
                  </div>
                  <FooterButton v-if="localeStore.countryIsUs" to="/company/careers">
                    {{ $t('footer.link.careers') }}
                  </FooterButton>
                  <FooterButton v-if="localeStore.countryIsCa || localeStore.countryIsQc || localeStore.countryIsDe || localeStore.countryIsAu" :href="$t('footer.link.coeUrl')" target="_blank" rel="noreferrer">
                    {{ $t('footer.link.coe') }}
                  </FooterButton>
                </FooterSection>
              </VCol>

              <VCol v-if="!isDemonstrator" cols="6" sm="3">
                <FooterSection :header="$t('footer.label.community')" :index="3">
                  <FooterButton v-if="!isSharedCart" to="/community/find-a-demonstrator">
                    {{ $t('footer.link.findADemo') }}
                  </FooterButton>
                  <FooterButton to="/community/join-stampin-up">
                    {{ $t('footer.link.join') }}
                  </FooterButton>
                  <FooterButton to="/community/stampin-rewards">
                    {{ $t('footer.link.stampinRewards') }}
                  </FooterButton>
                  <FooterButton to="/community/gathering">
                    {{ $t('footer.link.gathering') }}
                  </FooterButton>
                </FooterSection>
              </VCol>

              <VCol cols="6" :sm="isDemonstrator ? 4: 3" lg="3">
                <FooterSection :header="$t('footer.catalogs')" :index="4">
                  <FooterButton to="/catalogs/purchase-a-catalog">
                    {{ $t('footer.catalogsPurchase') }}
                  </FooterButton>
                  <FooterButton to="/catalogs/digital-catalogues">
                    {{ $t('footer.catalogsDigital') }}
                  </FooterButton>
                  <FooterButton to="/catalogs/corrections">
                    {{ $t('footer.link.corrections') }}
                  </FooterButton>
                </FooterSection>
              </VCol>
              <VCol v-if="isDemonstrator" cols="12" lg="3">
                <VDivider v-if="breakpoints.mdAndDown" class="mt-3 pb-9" />
                <FooterSocial class="d-flex justify-center" />
                <template v-if="showDsaLogo">
                  <!-- The badge is a direct-selling-association membership mark and the
                       only thing that states it, so it is content rather than decoration
                       and carries the country in its alt. Untranslated, like the other
                       organisation marks in the footer (FooterSocial, JoinOrderSummary). -->
                  <div class="pr-3 py-2 py-lg-4 d-flex justify-center" data-testid="dsa-logo">
                    <img v-if="localeStore.countryIsQc" :src="dsaBadge('DSA_QC.png')" alt="DSA Quebec" width="166px">
                    <img v-if="localeStore.countryIsAu" :src="dsaBadge('DSA_AU.png')" alt="DSA Australia" width="166px">
                    <img v-if="localeStore.countryIsFr" :src="dsaBadge('DSA_FR.png')" alt="DSA France" width="112px">
                    <img v-if="localeStore.countryIsDe" :src="dsaBadge('DSA_DE.png')" alt="DSA Germany" width="166px">
                    <img v-if="localeStore.countryIsCa && !localeStore.countryIsQc" :src="dsaBadge('DSA_CA.png')" alt="DSA Canada" width="166px">
                  </div>
                </template>
              </VCol>
            </VRow>
          </VCol>

          <VCol v-if="fifthColRendered" cols="12" lg="4" data-testid="footer-fifth-col">
            <VRow no-gutters justify="center" class="x-borders-md mt-8 mt-lg-0">
              <VCol v-if="newsletterSignupEnabled" cols="12" class="pt-8 pt-lg-0" :align="$vuetify.display.lgAndUp ? 'start' : 'center'">
                <NewsletterSignup />
              </VCol>
              <VCol v-if="newsletterSignupEnabled" cols="12" class="pb-8 pb-lg-0 py-1" :align="$vuetify.display.lgAndUp ? 'start' : 'center'">
                <FooterSocial class="pb-1" />
              </VCol>
              <!-- Desktop (lg+) placement: inside the fifth column, under the newsletter. -->
              <VCol v-if="cancelOrderRequestEnabled" cols="12" class="hidden-md-and-down pb-6" :class="newsletterSignupEnabled ? 'pt-4 pt-lg-3' : 'pt-0'" :align="$vuetify.display.lgAndUp ? 'start' : 'center'">
                <CancelOrderBlock />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
        <!-- Tablet/mobile (md and down) placement: its own centred row under the columns. -->
        <VRow v-if="cancelOrderRequestEnabled" no-gutters justify="center" class="hidden-lg-and-up py-6">
          <VCol cols="auto">
            <CancelOrderBlock />
          </VCol>
        </VRow>
        <VDivider v-if="isDemonstrator" class="hidden-lg-and-up mt-8" />
        <!-- Without a fifth column the link columns sit centred in 8/12, so the legal row is
             indented to the same left edge at lg+; with one, the columns span the full width
             and the legal row starts flush left with them. -->
        <VRow align="center" no-gutters>
          <VCol cols="12" class="legal-row" :class="{ 'legal-row--centered': !fifthColRendered }" data-testid="footer-legal-row-col" style="position: relative;">
            <VRow no-gutters>
              <VCol class="mt-n2 mt-lg-0">
                <FooterLegal />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
      </VContainer>
    </VContainer>
  </VFooter>
</template>

<script lang="ts">
import FooterSection from '~/components/layout/footer/FooterSection.vue'
import FooterLegal from '~/components/layout/footer/FooterLegal.vue'
import FooterSocial from '~/components/layout/footer/FooterSocial.vue'
import FooterButton from '~/components/layout/footer/FooterButton.vue'
import NewsletterSignup from '~/components/account/NewsletterSignup.vue'
import CancelOrderBlock from '~/components/layout/footer/CancelOrderBlock.vue'
import VHtml from '~/components/base/VHtml.vue'
import { useFeaturesStore } from '~/stores/features'
import { useLocaleStore } from '~/stores/locale'
import { useCartStore } from '~/stores/cart'
import { mediaAssetUrl } from '~/utils/format/mediaAssets'

export default defineComponent({
  name: 'AppFooter',
  components: {
    FooterButton,
    FooterLegal,
    FooterSection,
    FooterSocial,
    NewsletterSignup,
    CancelOrderBlock,
    VHtml
  },
  props: {
    hideTagline: {
      type: Boolean,
      default: false
    }
  },
  setup () {
    const featureStore = useFeaturesStore()

    const localeStore = useLocaleStore()
    const cartStore = useCartStore()
    const { breakpoints } = useBreakpoints()
    const isSharedCart = computed(() => cartStore.isSharedCartMode)
    const { isDemo : isDemonstrator } = useSAuthState()
    // DSA logo allow-list mirrors current nuxt2 (Qc/Au/Fr/De/Ca-not-Qc). Replaces
    // the old inverse hide-list, which rendered an empty wrapper (and a stray US
    // logo) for non-listed markets. NZ is intentionally excluded — verified in
    // production it does not display a DSA logo.
    const showDsaLogo = computed(() =>
      localeStore.countryIsQc ||
      localeStore.countryIsAu ||
      localeStore.countryIsFr ||
      localeStore.countryIsDe ||
      (localeStore.countryIsCa && !localeStore.countryIsQc),
    )

    // The badge keys are stored upper-cased (`DSA_QC.png`). Cloudinary served
    // the lowercase spellings too; the media host does not (ADO 62986).
    const dsaBadge = (key: string): string => mediaAssetUrl(`application_assets/${key}`)

    const newsletterSignupEnabled = computed(() => featureStore.newsletterSignupEnabled)
    const cancelOrderRequestEnabled = computed(() => featureStore.cancelOrderRequestEnabled)
    // The lg="4" column right of the link columns exists only for a non-demo session that
    // has something to put in it (newsletter signup and/or the EU cancel-order block).
    const fifthColRendered = computed(() =>
      !isDemonstrator.value && (newsletterSignupEnabled.value || cancelOrderRequestEnabled.value)
    )

    return {
      featureStore,
      dsaBadge,
      showDsaLogo,
      isSharedCart,
      isDemonstrator,
      localeStore,
      breakpoints,
      newsletterSignupEnabled,
      cancelOrderRequestEnabled,
      fifthColRendered
    }
  }
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

.background-grey {
  background: rgb(var(--v-theme-backgroundDiv));
}
.x-borders {
  border-top: 1px solid rgb(var(--v-theme-border));
  border-bottom: 1px solid rgb(var(--v-theme-border));
}
.tagline {
  width: 100%;
  height: 16px;
  // Override the global `img { object-fit: cover }` — `cover` crops the SVG
  // to the forced w×h box when aspect ratios differ. V2 had no such global
  // rule (img defaults to `fill`, which stretches the tagline to the box).
  object-fit: fill;
  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    height: 18px;
  }
}
@media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
  .x-borders-md {
    border-top: 1px solid rgb(var(--v-theme-border));
    border-bottom: 1px solid rgb(var(--v-theme-border));
  }
}

@media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
  .legal-row--centered {
    padding-left: calc((100% - 66.666%) / 2);
  }
}
// Mirrors FooterButton's non-legal link box (SBtn text variant: 0 16px default-size
// padding, 8px block margin, 14px/400, line-height 1, no text-transform).
.footer-link-suggestion :deep(a) {
  display: block;
  padding: 0 16px;
  margin: 8px 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: normal;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  text-transform: none;
}

.footer-flex {
  flex-wrap: wrap;
}
</style>
